// 학생 목록의 기존 검색·권한 범위를 유지한다. 레이팅은 별도 v2 API에서만 가져온다.
const fail = (message, httpStatus) => Object.assign(new Error(message), { httpStatus });

export async function fetchStudentListPage({ page, perPage, filters, userType, userId, signal }, getAccessToken) {
  const token = await getAccessToken();
  if (!token) throw fail('로그인이 필요합니다.', 401);
  if (!['manager', 'teacher'].includes(userType)) throw fail('접근 권한이 없습니다.', 403);
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  for (const key of ['student_name', 'school_name', 'in_charge_teacher', 'lecture']) {
    if (filters[key]) params.set(key, filters[key]);
  }
  const path = userType === 'teacher' ? '/api/users/student/allocate' : '/api/users/student';
  if (userType === 'teacher') params.set('teacher_id', userId);
  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${path}?${params}`, {
    signal, headers: { Authorization: token, 'Content-Type': 'application/json' },
  });
  if (response.status === 404 && page === 1) return { rows: [], totalPages: 1, totalItems: 0 };
  if (!response.ok) throw fail('학생 목록을 불러오지 못했습니다.', response.status);
  const result = await response.json();
  const totalPages = Math.max(1, Number(result.total_pages ?? 1));
  const totalItems = result.total_items ?? result.total_count ?? null;
  if (!Array.isArray(result.data) || !Number.isInteger(totalPages)
      || (totalItems != null && (!Number.isInteger(Number(totalItems)) || Number(totalItems) < 0))) {
    throw fail('학생 목록 응답을 확인할 수 없습니다.');
  }
  return { rows: result.data, totalPages, totalItems: totalItems == null ? null : Number(totalItems) };
}

/** 한 페이지라도 실패하거나 목록이 바뀌면 부분 목록으로 전체 정렬을 하지 않는다. */
export async function fetchAllStudentList(options, getAccessToken) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  options.signal?.addEventListener('abort', abort, { once: true });
  const checkAborted = () => {
    if (controller.signal.aborted || options.signal?.aborted) throw new DOMException('조회 취소', 'AbortError');
  };
  try {
    checkAborted();
    // 기존 API/검색 조건 유지. 요청 수를 줄이고 나머지 페이지는 최대 2개씩 조회한다.
    const params = { ...options, signal: controller.signal, perPage: 200 };
    const first = await fetchStudentListPage({ ...params, page: 1 }, getAccessToken);
    checkAborted();
    const parts = [first.rows];
    let nextPage = 2;
    const worker = async () => {
      while (nextPage <= first.totalPages) {
        checkAborted();
        const page = nextPage++;
        const part = await fetchStudentListPage({ ...params, page }, getAccessToken);
        checkAborted();
        if (part.totalPages !== first.totalPages || part.totalItems !== first.totalItems || part.rows.length === 0) {
          throw fail('조회 중 학생 목록이 바뀌었습니다. 다시 조회해주세요.');
        }
        parts[page - 1] = part.rows;
      }
    };
    await Promise.all(Array.from({ length: Math.min(2, first.totalPages - 1) }, worker));
    const rows = parts.flat();
    const ids = rows.map((row) => Number(row.student_id));
    if (ids.some((id) => !Number.isInteger(id) || id <= 0) || new Set(ids).size !== ids.length
        || (first.totalItems != null && rows.length !== first.totalItems)) {
      throw fail('학생 목록을 모두 확인하지 못했습니다. 다시 조회해주세요.');
    }
    return rows;
  } catch (error) {
    controller.abort();
    throw error;
  } finally {
    options.signal?.removeEventListener('abort', abort);
  }
}
